<template>
  <div>
    <div class="d-flex align-center">
      <h1 class="flex-grow-1">{{ $t('routes.pod.authenticity.title') }}</h1>
      <v-tooltip location="bottom" :aria-label="$t('routes.pod.authenticity.helpTitle')">
        <template v-slot:activator="{ props }">
          <v-btn v-bind="props"
                 icon="mdi-help-circle-outline"
                 variant="text"
                 size="32"
                 @click="showHelpDialog = true"
                 :aria-label="$t('routes.pod.authenticity.helpTitle')"
          >
            <v-icon size="small">mdi-help-circle-outline</v-icon>
          </v-btn>
        </template>
        {{ $t('routes.pod.authenticity.helpTitle') }}
      </v-tooltip>
    </div>

    <!-- Run Check button (top, visible when no pending) -->
    <v-btn
      color="primary"
      variant="flat"
      block
      class="mt-4"
      :disabled="isCheckDisabled"
      :loading="isCheckingUtility"
      @click="onRunCheckClick"
    >
      <v-icon class="mr-1">mdi-shield-check</v-icon>
      {{ $t('routes.pod.authenticity.runCheck') }}
    </v-btn>

    <!-- Show unsupported format info -->
    <v-alert v-if="!isSightengineFormatSupported && !isVideo && !utilityError"
      type="warning" variant="tonal" density="compact" class="mt-2">
      {{ $t('routes.pod.authenticity.unsupportedImageFormat') }}
    </v-alert>

    <!-- Show utility error near the button when it's disabled and we have results -->
    <v-alert v-if="!canRunCheck && utilityError && hasSummaryData && !isCheckingUtility"
      type="warning" variant="tonal" density="compact" class="mt-2">
      {{ utilityError }}
    </v-alert>

    <!-- Description & credits info (only when no checks yet) -->
    <div v-if="!hasSummaryData && !isLoading" class="mt-4">
      <p class="text-body-1 text-medium-emphasis mb-4">
        {{ $t('routes.pod.authenticity.description') }}
      </p>

      <div v-if="utilityError" class="mb-3">
        <v-alert type="warning" variant="tonal" density="compact">
          {{ utilityError }}
        </v-alert>
      </div>

    </div>

    <!-- Credit info -->
    <v-sheet v-if="metadata && !isCheckingUtility" rounded="lg" class="credits-sheet pa-3 mt-2">
      <div class="text-body-1 text-medium-emphasis">
        {{ $t('routes.pod.authenticity.creditsAvailable', { credits: metadata.available }) }}
      </div>
      <div v-if="isVideo && metadata.creditsPerMinute" class="text-body-1 text-medium-emphasis mt-1">
        {{ $t('routes.pod.authenticity.creditsRate', { credits: metadata.creditsPerMinute }) }}
      </div>
      <div v-if="!isVideo" class="text-body-1 text-medium-emphasis mt-1">
        {{ $t('routes.pod.authenticity.creditsRateImage', { credits: metadata.cost }) }}
      </div>
    </v-sheet>

    <!-- <v-alert v-if="showInsufficientCredits"
      type="warning" variant="tonal" density="compact" class="mt-2">
      {{ $t('routes.pod.authenticity.notEnoughCredits') }}
    </v-alert> -->

    <div v-if="showInsufficientCredits" class="my-2">
      <template v-if="$canPurchaseResources()">
        <div>{{ $t('routes.pod.authenticity.wantToBuyBundle') }}</div>
        <div class="d-flex justify-space-around mt-2">
          <v-btn @click="hideInsufficientCreditsMessage" elevation="0" color="primary">
            {{ $t('common.no') }}
          </v-btn>
          <v-btn @click="navigateToUsage" elevation="0" color="primary">
            {{ $t('common.yes') }}
          </v-btn>
        </div>
      </template>
      <template v-else>
        <div class="bg-warning pa-2">{{ $t('common.contactTenantAdminForCredits') }}</div>
      </template>
    </div>

    <!-- Video Forensic Analysis button (video pods only) -->
    <v-btn
      v-if="isVideo && !isLoading"
      color="secondary"
      variant="tonal"
      block
      class="mt-3"
      :disabled="videoMetadataAnalysis !== null || isRunningForensic"
      :loading="isRunningForensic"
      @click="runForensicAnalysis"
    >
      <template v-slot:loader>
        <v-progress-circular indeterminate color="primary" :aria-label="$t('routes.nodes.loading')" />
      </template>
      <v-icon class="mr-1">mdi-magnify-scan</v-icon>
      <template v-if="videoMetadataAnalysis">
        {{ $t('routes.pod.authenticity.forensicCompleted') }}
      </template>
      <template v-else>
        {{ $t('routes.pod.authenticity.runForensicAnalysis') }}
      </template>
    </v-btn>

    <!-- Loading state -->
    <div v-if="isLoading" class="d-flex justify-center mt-6">
      <v-progress-circular indeterminate color="primary" :aria-label="$t('routes.nodes.loading')" />
    </div>

    <template v-else-if="hasSummaryData">
      <!-- Aggregate Summary -->
      <v-card variant="outlined" class="mt-3">
        <v-card-text>
          <div class="d-flex align-center justify-space-between mb-2">
            <span class="text-subtitle-1 font-weight-bold">
              {{ $t('routes.pod.authenticity.summaryTitle') }}
            </span>
            <v-chip v-if="aggregateStatus" :color="aggregateStatusColor" variant="flat" size="small">
              {{ aggregateStatusLabel }}
            </v-chip>
          </div>
          <div class="detail-row">
            <span class="text-medium-emphasis">
              {{ $t('routes.pod.authenticity.scorecard.authenticity') }}
            </span>
            <span class="font-weight-bold" :style="{ color: averageAuthenticityColor }">
              {{ averageAuthenticityPercentage != null ? averageAuthenticityPercentage + '%' : '-' }}
            </span>
          </div>
          <div class="detail-row">
            <span class="text-medium-emphasis">
              {{ $t('routes.pod.authenticity.scorecard.classification') }}
            </span>
            <span>{{ aggregateClassificationLabel }}</span>
          </div>
          <div class="detail-row">
            <span class="text-medium-emphasis">
              {{ $t('routes.pod.authenticity.checksCompleted') }}
            </span>
            <span>{{ totalChecks }}</span>
          </div>
          <div class="detail-row">
            <span class="text-medium-emphasis">
              {{ $t('routes.pod.authenticity.creditsUsed') }}
            </span>
            <span>{{ totalCreditsUsed }}</span>
          </div>
        </v-card-text>
      </v-card>

      <!-- Pending checks -->
      <div v-if="summaryData.pendingChecks.length > 0" class="mt-4">
        <v-alert variant="tonal" density="compact">
          <div class="d-flex align-center">
            <v-progress-circular indeterminate size="18" width="2" class="mr-2" :aria-label="analyzingMessage" />
            {{ analyzingMessage }}
          </div>
        </v-alert>
      </div>

      <!-- Full scans -->
      <div v-for="scan in summaryData.fullScans" :key="'full-' + scan.id">
        <AuthenticityScorecard :check="scan" :is-video="isVideo" />
      </div>

      <!-- Segment analysis -->
      <div v-if="summaryData.segmentAnalysis">
        <AuthenticitySegmentScorecard :analysis="summaryData.segmentAnalysis" />
      </div>

      <!-- Metadata analysis -->
      <AuthenticityMetadataScorecard
        v-if="metadataAnalysis && !isVideo"
        :metadata-analysis="metadataAnalysis"
      />

      <!-- Video metadata analysis -->
      <AuthenticityVideoMetadataScorecard
        v-if="videoMetadataAnalysis"
        :video-metadata-analysis="videoMetadataAnalysis"
      />
    </template>

    <!-- Confirmation dialog -->
    <AuthenticityCheckDialog
      v-model="showConfirmDialog"
      :cost="metadata ? metadata.cost : 0"
      :available="metadata ? metadata.available : null"
      :loading="isRunning"
      :is-video="isVideo"
      :video-duration="metadata ? metadata.videoDuration : 0"
      :full-video-cost="metadata ? metadata.cost : 0"
      :max-affordable-minutes="metadata ? metadata.maxAffordableMinutes : null"
      :credits-per-minute="metadata ? (metadata.creditsPerMinute || 100) : 100"
      :completed-models="metadata ? (metadata.completedModels || []) : []"
      :pending-models="pendingModels"
      @confirm="runCheck"
      @cancel="showConfirmDialog = false"
    />

    <!-- Help dialog -->
    <AuthenticityHelpDialog
      v-model="showHelpDialog"
      :has-forensic-details="hasForensicDetails"
    />
  </div>
</template>

<script>
import { useServiceStore } from '@/stores/ServiceStore.js';
import Api from '@/api/Api.js';
import MimetypeHelper from '@/components/MimetypeHelper.js';
import AuthenticityScorecard from './AuthenticityScorecard.vue';
import AuthenticitySegmentScorecard from './AuthenticitySegmentScorecard.vue';
import AuthenticityCheckDialog from './AuthenticityCheckDialog.vue';
import AuthenticityHelpDialog from './AuthenticityHelpDialog.vue';
import AuthenticityMetadataScorecard from './AuthenticityMetadataScorecard.vue';
import AuthenticityVideoMetadataScorecard from './AuthenticityVideoMetadataScorecard.vue';

export default {
  name: 'AuthenticityToolbar',
  components: {
    AuthenticityScorecard,
    AuthenticitySegmentScorecard,
    AuthenticityCheckDialog,
    AuthenticityHelpDialog,
    AuthenticityMetadataScorecard,
    AuthenticityVideoMetadataScorecard,
  },
  props: {
    pod: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      isLoading: false,
      isCheckingUtility: false,
      isRunning: false,
      showConfirmDialog: false,
      showHelpDialog: false,
      summaryData: null,
      metadataAnalysis: null,
      videoMetadataAnalysis: null,
      combinedAuthenticity: null,
      serverSummary: null,
      metadata: null,
      canRunCheck: false,
      utilityError: null,
      pollTimer: null,
      isRunningForensic: false,
      forensicPollTimer: null,
      isDestroyed: false,
      isInsufficientCreditsDismissed: false,
    };
  },
  computed: {
    isVideo() {
      return MimetypeHelper.isMimetypeVideo(this.pod);
    },
    isSightengineFormatSupported() {
      if (this.isVideo) return true;
      const mimetype = this.pod?.asset?.raw?.[0]?.mimetype;
      if (!mimetype) return true;
      return [
        'image/png',
        'image/jpeg',
        'image/gif',
        'image/webp',
        'image/bmp',
        'image/tiff',
        'image/vnd.adobe.photoshop',
      ].includes(mimetype);
    },
    isCheckDisabled() {
      if (!this.hasEnoughCredits) return true;
      if (!this.isSightengineFormatSupported) return true;
      if (this.isVideo) return false;

      // For images, require both genai and deepfake models to be completed before allowing checks,
      // since image analysis relies heavily on both.
      const completedModels = this.metadata?.completedModels || [];
      return completedModels.includes('genai') && completedModels.includes('deepfake');
    },
    analyzingMessage() {
      const key = this.isVideo
        ? 'routes.pod.authenticity.analyzingVideo'
        : 'routes.pod.authenticity.analyzing';
      return this.$t(key);
    },
    pollInterval() {
      if (!this.isVideo) {
        return 5000;
      }

      const rawAsset = this.pod?.asset?.raw?.[0];
      const sizeBytes = rawAsset ? Number(rawAsset.size) : 0;
      const HUNDRED_MB = 100 * 1024 * 1024;

      return sizeBytes < HUNDRED_MB ? 5000 : 60000;
    },
    hasPendingChecks() {
      return this.summaryData?.pendingChecks?.length > 0;
    },
    pendingModels() {
      return (this.summaryData?.pendingChecks || [])
        .map((c) => c.forensicdetails?.model || 'genai');
    },
    aggregateStatus() {
      if (this.serverSummary?.aggregateStatus) return this.serverSummary.aggregateStatus;
      const statuses = [];
      const scans = this.summaryData?.fullScans || [];
      statuses.push(...scans.map((s) => s.status));
      if (this.summaryData?.segmentAnalysis?.status) {
        statuses.push(this.summaryData.segmentAnalysis.status);
      }
      if (statuses.length === 0) return null;
      if (statuses.includes('AI_GENERATED')) return 'AI_GENERATED';
      if (statuses.includes('MANIPULATED')) return 'MANIPULATED';
      if (statuses.includes('SUSPICIOUS')) return 'SUSPICIOUS';
      return 'AUTHENTIC';
    },
    aggregateStatusLabel() {
      if (!this.aggregateStatus) return '-';
      return this.$t(`routes.pod.authenticity.status.${this.aggregateStatus}`);
    },
    aggregateStatusColor() {
      switch (this.aggregateStatus) {
        case 'AUTHENTIC': return 'success';
        case 'SUSPICIOUS': return 'warning';
        case 'AI_GENERATED': return 'error';
        case 'MANIPULATED': return 'error';
        default: return 'grey';
      }
    },
    averageAuthenticityPercentage() {
      if (this.serverSummary?.authenticityPercentage != null) return this.serverSummary.authenticityPercentage;
      if (this.combinedAuthenticity != null) {
        return this.combinedAuthenticity;
      }
      const scores = [];
      const scans = this.summaryData?.fullScans?.filter((s) => s.overallscore != null) || [];
      scores.push(...scans.map((s) => 100 - s.overallscore));
      if (this.summaryData?.segmentAnalysis?.overallscore != null) {
        scores.push(100 - this.summaryData.segmentAnalysis.overallscore);
      }
      if (scores.length === 0) return null;
      const avg = scores.reduce((sum, v) => sum + v, 0) / scores.length;
      return Math.round(avg);
    },
    averageAuthenticityColor() {
      const pct = this.averageAuthenticityPercentage;
      if (pct == null) return undefined;
      if (pct >= 70) return 'rgb(var(--v-theme-success))';
      if (pct >= 30) return 'rgb(var(--v-theme-warning))';
      return 'rgb(var(--v-theme-error))';
    },
    aggregateClassificationLabel() {
      if (this.serverSummary?.classificationLabel) return this.formatCategoryLabel(this.serverSummary.classificationLabel);
      const scans = this.summaryData?.fullScans || [];
      const classifications = scans.map((s) => s.aiclassification).filter(Boolean);
      if (this.summaryData?.segmentAnalysis?.aiclassification) {
        classifications.push(this.summaryData.segmentAnalysis.aiclassification);
      }
      if (classifications.length === 0) return '-';
      const priority = ['likely_ai_generated', 'possibly_ai_generated', 'likely_authentic'];
      for (const level of priority) {
        if (classifications.includes(level)) return this.formatCategoryLabel(level);
      }
      return this.formatCategoryLabel(classifications[0]);
    },
    totalChecks() {
      if (this.serverSummary?.totalChecks != null) return this.serverSummary.totalChecks;
      const fullCount = this.summaryData?.fullScans?.length || 0;
      const segCount = this.summaryData?.segmentAnalysis?.segments?.length || 0;
      return fullCount + segCount;
    },
    totalCreditsUsed() {
      if (this.serverSummary?.totalCreditsUsed != null) return this.serverSummary.totalCreditsUsed;
      const scans = this.summaryData?.fullScans || [];
      const fullCredits = scans.reduce((sum, s) => sum + (s.creditsused || 0), 0);
      const segCredits = this.summaryData?.segmentAnalysis?.totalCreditsUsed || 0;
      return fullCredits + segCredits;
    },
    hasSummaryData() {
      if (!this.summaryData) return false;
      return this.summaryData.fullScans.length > 0
        || this.summaryData.segmentAnalysis != null
        || this.summaryData.pendingChecks.length > 0
        || this.metadataAnalysis != null
        || this.videoMetadataAnalysis != null;
    },
    hasForensicDetails() {
      if (!this.summaryData) return false;
      const fullHas = this.summaryData.fullScans.some((s) => !!s.forensicdetails);
      const segHas = (this.summaryData.segmentAnalysis?.segments || [])
        .some((seg) => seg.forensicFrames && seg.forensicFrames.length > 0);
      return fullHas || segHas;
    },
    hasEnoughCredits() {
      if (this.isVideo && this.metadata?.maxAffordableMinutes > 0) {
        return true;
      }

      return this.metadata?.cost <= this.metadata?.available;
    },
    showInsufficientCredits() {
      return !!this.metadata
        && !this.isCheckingUtility
        && !this.hasEnoughCredits
        && !this.isInsufficientCreditsDismissed;
    },
  },
  beforeUnmount() {
    this.isDestroyed = true;
    this.stopPolling();
    this.stopForensicPolling();
  },
  mounted() {
    this.loadSummary();
  },
  watch: {
    'pod.id': function() {
      this.stopPolling();
      this.isInsufficientCreditsDismissed = false;
      this.loadSummary();
    },
    pod: {
      handler(newPod, oldPod) {
        if (newPod?.id !== oldPod?.id) return;
        if (this.hasPendingChecks) {
          this.loadSummary();
        }
      },
      deep: false,
    },
  },
  methods: {
    async loadSummary() {
      if (!this.pod?.id) return;

      this.isLoading = true;
      this.summaryData = null;
      this.metadataAnalysis = null;
      this.videoMetadataAnalysis = null;
      this.combinedAuthenticity = null;
      this.serverSummary = null;
      this.utilityError = null;

      try {
        const response = await Api.authenticityCheck.getSummary(this.pod.id);
        if (this.isDestroyed) return;
        if (response.data) {
          this.summaryData = {
            fullScans: response.data.fullScans || [],
            segmentAnalysis: response.data.segmentAnalysis || null,
            pendingChecks: response.data.pendingChecks || [],
          };
          this.metadataAnalysis = response.data.metadataAnalysis || null;
          this.videoMetadataAnalysis = response.data.videoMetadataAnalysis || null;
          this.combinedAuthenticity = response.data.combinedAuthenticity ?? null;
          this.serverSummary = response.data.summary || null;

          if (this.summaryData.pendingChecks.length > 0) {
            this.startPolling();
          }

          // Always load utility metadata so the top button works.
          await this.checkUtility();
        } else {
          await this.checkUtility();
        }
      } catch (err) {
        console.error('Error loading authenticity summary:', err);
        await this.checkUtility();
      } finally {
        if (!this.isDestroyed) this.isLoading = false;
      }
    },

    async checkUtility() {
      this.isCheckingUtility = true;
      this.canRunCheck = false;
      this.utilityError = null;

      try {
        const serviceStore = useServiceStore();
        const result = await serviceStore.updatePodsUtility({
          serviceAlias: 'authenticity-check',
          pods: [this.pod],
        });

        if (this.isDestroyed) return;
        if (result && result.pods && result.pods.length > 0) {
          const podInfo = result.pods[0];
          this.canRunCheck = podInfo.info?.runnable ?? false;
          this.metadata = result.metadata ?? podInfo.metadata ?? null;

          if (!this.canRunCheck && podInfo.error) {
            this.utilityError = typeof podInfo.error === 'string'
              ? podInfo.error
              : podInfo.error?.content ?? podInfo.error?.message ?? podInfo.error?.text ?? null;
          }
        }
      } catch (err) {
        console.error('Error checking authenticity utility:', err);
      } finally {
        if (!this.isDestroyed) this.isCheckingUtility = false;
      }
    },

    onRunCheckClick() {
      this.showConfirmDialog = true;
    },

    navigateToUsage() {
      this.$router.push('/console/usage');
    },

    hideInsufficientCreditsMessage() {
      this.isInsufficientCreditsDismissed = true;
    },

    startPolling() {
      this.stopPolling();
      this.pollTimer = setInterval(async () => {
        try {
          const response = await Api.authenticityCheck.getSummary(this.pod.id);
          if (this.isDestroyed) return;
          if (response.data) {
            this.summaryData = {
              fullScans: response.data.fullScans || [],
              segmentAnalysis: response.data.segmentAnalysis || null,
              pendingChecks: response.data.pendingChecks || [],
            };
            this.metadataAnalysis = response.data.metadataAnalysis || null;
          this.videoMetadataAnalysis = response.data.videoMetadataAnalysis || null;
            this.combinedAuthenticity = response.data.combinedAuthenticity ?? null;
            this.serverSummary = response.data.summary || null;

            if (this.summaryData.pendingChecks.length === 0) {
              this.stopPolling();
              // Delay to allow BullMQ job to fully transition out of 'active' state
              // before re-checking service availability.
              setTimeout(() => {
                if (this.isDestroyed) return;
                this.checkUtility();
              }, 3000);
            }
          }
        } catch (err) {
          console.error('Error polling authenticity summary:', err);
        }
      }, this.pollInterval);
    },

    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },

    async runCheck(segmentData = {}) {
      this.isRunning = true;

      try {
        const serviceStore = useServiceStore();
        const runnablePods = serviceStore.getRunnablePods;

        if (runnablePods.length === 0) {
          if (this.utilityError) {
            this.$showMessage(this.utilityError, { type: 'warning' });
          }
          this.showConfirmDialog = false;
          await this.checkUtility();
          return;
        }

        const params = {};
        if (segmentData.rangeMode === 'custom') {
          params.startSecond = segmentData.startSecond;
          params.endSecond = segmentData.endSecond;
        }
        if (segmentData.model) {
          params.model = segmentData.model;
        }

        await serviceStore.runService({
          serviceAlias: 'authenticity-check',
          utilityPods: runnablePods,
          params,
        });

        this.utilityError = null;
        this.showConfirmDialog = false;

        // Add a pending placeholder and start polling.
        if (!this.summaryData) {
          this.summaryData = { fullScans: [], segmentAnalysis: null, pendingChecks: [] };
        }
        this.summaryData.pendingChecks.push({
          status: 'PENDING',
          forensicdetails: { model: segmentData.model || 'genai' },
        });
        this.startPolling();
      } catch (err) {
        console.error('Error running authenticity check:', err);
        const errorCode = err?.response?.data?.error?.code;
        if (errorCode === 'ERR212') {
          this.showConfirmDialog = false;

          const localizedMessage = this.$localizeMessage(err?.response?.data?.error);
          if (localizedMessage) {
            this.$showMessage(localizedMessage, { type: 'warning' });
          }

          await this.loadSummary();
          return;
        }
        const errorMsg = err?.response?.data?.error?.content
          || err?.response?.data?.error?.message
          || err?.response?.data?.message
          || err?.message;
        if (errorMsg) {
          this.$showMessage(errorMsg, { type: 'warning' });
        }
        this.showConfirmDialog = false;
      } finally {
        this.isRunning = false;
      }
    },

    async runForensicAnalysis() {
      this.isRunningForensic = true;
      try {
        const serviceStore = useServiceStore();

        const result = await serviceStore.updatePodsUtility({
          serviceAlias: 'video-forensic-analysis',
          pods: [this.pod],
        });

        const runnablePods = serviceStore.getRunnablePods;
        if (runnablePods.length === 0) {
          if (result && result.pods && result.pods.length > 0) {
            const podInfo = result.pods[0];
            if (podInfo.error) {
              this.$showMessage(this.$localizeMessage(podInfo.error), { type: 'warning' });
            }
          }
          this.isRunningForensic = false;
          return;
        }

        await serviceStore.runService({
          serviceAlias: 'video-forensic-analysis',
          utilityPods: runnablePods,
        });

        this.startForensicPolling();
      } catch (err) {
        console.error('Error running forensic analysis:', err);
        this.isRunningForensic = false;
      }
    },

    startForensicPolling() {
      this.stopForensicPolling();
      this.forensicPollTimer = setInterval(async () => {
        try {
          const response = await Api.authenticityCheck.getSummary(this.pod.id);
          if (this.isDestroyed) return;
          if (response.data?.videoMetadataAnalysis) {
            this.videoMetadataAnalysis = response.data.videoMetadataAnalysis;
            this.combinedAuthenticity = response.data.combinedAuthenticity ?? null;
            this.serverSummary = response.data.summary || null;
            this.isRunningForensic = false;
            this.stopForensicPolling();
          }
        } catch (err) {
          console.error('Error polling forensic analysis:', err);
        }
      }, 5000);
    },

    stopForensicPolling() {
      if (this.forensicPollTimer) {
        clearInterval(this.forensicPollTimer);
        this.forensicPollTimer = null;
      }
    },

    formatCategoryLabel(key) {
      return key
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (char) => char.toUpperCase());
    },
    formatDate(dateStr) {
      if (!dateStr) return '';
      const d = new Date(dateStr);
      return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    },
  },
};
</script>

<style scoped>
.credits-sheet {
  background-color: rgba(var(--v-theme-on-surface), 0.06);
}

.detail-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 0;
}
</style>
